import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAzureDevOpsCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import type { AzureDevOpsRequest } from '../../api/types'
import { TrustBar } from '../shared/TrustBar'
import { ShareButton } from '../shared/ShareButton'
import { DiscountModifier } from '../shared/DiscountModifier'
import { FieldTooltip } from '../shared/FieldTooltip'
import { ScenarioPresets } from '../shared/ScenarioPresets'

const DEFAULT_INPUTS: Required<AzureDevOpsRequest> = {
  microsoftHostedJobs: 1,
  selfHostedJobs: 0,
  microsoftHostedMinutesPerMonth: 1800,
  basicUsers: 10,
  testPlanUsers: 0,
  artifactStorageGB: 2,
  managedPoolsEnabled: false,
}

function fmt(n: number) {
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function NumInput({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  hint,
  tooltipContent,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
  step?: number
  hint?: string
  tooltipContent?: string
}) {
  return (
    <div>
      <label className="block text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
        {label}{tooltipContent && <FieldTooltip content={tooltipContent} />}
      </label>
      {hint && <p className="text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Math.max(min, Number(e.target.value)))}
        className="w-full rounded px-3 py-2 text-[13px] border"
        style={{
          backgroundColor: 'var(--surface-container)',
          borderColor: 'var(--outline-variant)',
          color: 'var(--text-primary)',
        }}
      />
    </div>
  )
}

export default function AzureDevOpsCalc() {
  const [inputs, setInputs] = useState<Required<AzureDevOpsRequest>>(DEFAULT_INPUTS)
  const [discount, setDiscount] = useState(0)
  const { data, isLoading, isError } = useAzureDevOpsCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()
  useEffect(() => {
    if (data?.totalMonthly) {
      trackCalculatorUsed({ calculator: 'azure-devops', result_gbp: data.totalMonthly })
    }
  }, [data?.totalMonthly])

  function set<K extends keyof AzureDevOpsRequest>(key: K, value: AzureDevOpsRequest[K]) {
    setInputs((prev) => ({ ...prev, [key]: value }))
  }

  const total = data?.totalMonthly ?? 0
  const discounted = discount > 0 ? total * (1 - discount / 100) : null

  const freeMinutes = data?.breakdown.parallelJobs.microsoftHosted.freeMinutesApplied ?? 0
  const minutesPct = inputs.microsoftHostedJobs >= 1
    ? Math.min(100, (inputs.microsoftHostedMinutesPerMonth / 1800) * 100)
    : 0

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* ── Left column: inputs ── */}
      <div className="flex flex-col gap-6">
        <ScenarioPresets presets={[
          {
            label: 'Solo developer',
            description: '1 MS-hosted job · 1,800 min/mo · 1 Basic user — typically £0/mo',
            apply: () => { set('microsoftHostedJobs', 1); set('microsoftHostedMinutesPerMonth', 1800); set('selfHostedJobs', 0); set('basicUsers', 1); set('testPlanUsers', 0); },
          },
          {
            label: 'Small team (10 devs)',
            description: '2 MS-hosted jobs · 5,000 min/mo · 10 Basic users',
            apply: () => { set('microsoftHostedJobs', 2); set('microsoftHostedMinutesPerMonth', 5000); set('selfHostedJobs', 0); set('basicUsers', 10); set('testPlanUsers', 0); },
          },
          {
            label: 'Enterprise (50 devs + test)',
            description: '5 MS-hosted + 2 self-hosted jobs · 50 Basic + 10 Test Plans users',
            apply: () => { set('microsoftHostedJobs', 5); set('microsoftHostedMinutesPerMonth', 20000); set('selfHostedJobs', 2); set('basicUsers', 50); set('testPlanUsers', 10); },
          },
        ]} />
        {/* Parallel jobs section */}
        <section
          className="rounded-lg p-5"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <h2 className="text-[13px] font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-tertiary)' }}>
            Parallel Jobs
          </h2>

          {/* Parallel jobs explainer */}
          <div
            className="rounded p-3 mb-4 text-[12px] leading-relaxed"
            style={{ backgroundColor: 'var(--surface-container-low)', borderLeft: '3px solid #0078d4', color: 'var(--text-secondary)' }}
          >
            <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>What is a parallel job?</p>
            <p className="mb-1">
              A parallel job is the ability to run one pipeline at a time — not the number of VMs or agents. With 1 parallel job, pipelines run sequentially. With 2, you can run 2 simultaneously. Most small teams need only 1.
            </p>
            <p>
              <span className="font-medium">Free tier:</span> The first Microsoft-hosted job includes 1,800 free minutes/month (about 30 hours). This resets monthly. Additional jobs have no free minutes.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <NumInput
              label="Microsoft-hosted parallel jobs"
              tooltipContent="Each parallel job lets one pipeline run at a time. Add jobs to run multiple pipelines simultaneously. First job is free."
              value={inputs.microsoftHostedJobs}
              onChange={(v) => set('microsoftHostedJobs', v)}
              min={0}
              max={100}
              hint="First job = 1,800 min/month free. Each additional job: £30.19/month."
            />

            {/* Free minutes indicator */}
            {inputs.microsoftHostedJobs >= 1 && (
              <div>
                <div className="flex justify-between text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>
                  <span>Free minutes used this estimate</span>
                  <span>{inputs.microsoftHostedMinutesPerMonth.toLocaleString()} / 1,800 min/month</span>
                </div>
                <div className="rounded-full overflow-hidden h-2" style={{ backgroundColor: 'var(--outline-variant)' }}>
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, minutesPct)}%`,
                      backgroundColor: minutesPct > 100 ? '#dc2626' : '#0078d4',
                    }}
                  />
                </div>
                <NumInput
                  label="Pipeline minutes per month"
                  value={inputs.microsoftHostedMinutesPerMonth}
                  onChange={(v) => set('microsoftHostedMinutesPerMonth', v)}
                  min={0}
                  hint={`${freeMinutes.toLocaleString()} free minutes applied to first job.`}
                />
              </div>
            )}

            <NumInput
              label="Self-hosted parallel jobs"
              tooltipContent="Run pipelines on your own machines or VMs. First job is always free; additional self-hosted jobs cost less than Microsoft-hosted."
              value={inputs.selfHostedJobs}
              onChange={(v) => set('selfHostedJobs', v)}
              min={0}
              max={100}
              hint="First self-hosted job: FREE (unlimited minutes). Each additional: £11.32/month."
            />
          </div>
        </section>

        {/* Users section */}
        <section
          className="rounded-lg p-5"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <h2 className="text-[13px] font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-tertiary)' }}>
            Users
          </h2>

          {/* User pricing callout */}
          <div
            className="rounded p-3 mb-4 text-[12px] leading-relaxed"
            style={{ backgroundColor: 'var(--surface-container-low)', borderLeft: '3px solid #0078d4', color: 'var(--text-secondary)' }}
          >
            <p>
              <span className="font-medium">First 5 users are always free.</span> Azure DevOps includes 5 Basic users at no cost regardless of organisation size. Stakeholder access (read-only boards and backlogs) is always free and unlimited — only count users who need to create/edit work items, run pipelines, or access repos.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <NumInput
              label="Basic users (total)"
              value={inputs.basicUsers}
              onChange={(v) => set('basicUsers', v)}
              min={0}
              hint="First 5 free. Additional: £4.53/user/month. Includes Pipelines, Boards, Repos."
            />
            <NumInput
              label="Test Plans users"
              value={inputs.testPlanUsers}
              onChange={(v) => set('testPlanUsers', v)}
              min={0}
              hint="£39.25/user/month. Only for QA engineers using full Test Plans features."
            />
          </div>
        </section>

        {/* Artifact storage */}
        <section
          className="rounded-lg p-5"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <h2 className="text-[13px] font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-tertiary)' }}>
            Artifact Storage
          </h2>
          <NumInput
            label="Artifact storage (GB)"
            value={inputs.artifactStorageGB}
            onChange={(v) => set('artifactStorageGB', v)}
            min={0}
            step={0.5}
            hint="First 2 GB free. Additional: £1.51/GB/month. Feeds, packages, and pipeline artifacts all count."
          />
        </section>

        {/* Managed DevOps Pools */}
        <section
          className="rounded-lg p-5"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <h2 className="text-[13px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-tertiary)' }}>
            Managed DevOps Pools
          </h2>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={inputs.managedPoolsEnabled}
              onChange={(e) => set('managedPoolsEnabled', e.target.checked)}
              className="w-4 h-4 rounded"
              style={{ accentColor: '#0078d4' }}
            />
            <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
              Using Managed DevOps Pools
            </span>
          </label>
          {inputs.managedPoolsEnabled && (
            <div
              className="mt-3 rounded p-3 text-[12px] leading-relaxed"
              style={{ backgroundColor: 'var(--surface-container-low)', borderLeft: '3px solid #f59e0b', color: 'var(--text-secondary)' }}
            >
              <p className="mb-1">
                <span className="font-medium">Managed DevOps Pools</span> use Azure VMs as the underlying compute. The VM cost depends on the size you select — typically £0.10–£0.50/hour per agent.
              </p>
              <Link to="/virtual-machines" className="font-medium hover:underline" style={{ color: '#0078d4' }}>
                Estimate your VM cost →
              </Link>
              <span className="ml-1">Add the estimated VM cost to this total for a complete picture.</span>
            </div>
          )}
        </section>

        <TrustBar />
      </div>

      {/* ── Right column: result ── */}
      <div className="flex flex-col gap-4">
        {data?.minutesWarning && (
          <div
            className="rounded-lg p-4 text-[12px] leading-relaxed"
            style={{ backgroundColor: 'var(--surface-container)', border: '1px solid #f59e0b', color: 'var(--text-secondary)' }}
          >
            <p className="font-semibold mb-1" style={{ color: '#f59e0b' }}>Minutes usage warning</p>
            <p>{data.minutesWarning}</p>
          </div>
        )}

        <div
          className="rounded-lg p-6"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <p className="text-[12px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--text-tertiary)' }}>
            Monthly estimate (PAYG)
          </p>
          {isLoading && (
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>Calculating…</p>
          )}
          {isError && (
            <p className="text-[13px] text-red-500">Error — check inputs and try again.</p>
          )}
          {data && !isLoading && (
            <>
              <p className="text-3xl font-bold mb-1" style={{ color: '#0078d4' }}>
                £{fmt(discounted ?? total)}
                <span className="text-[14px] font-normal ml-1" style={{ color: 'var(--text-tertiary)' }}>/month</span>
              </p>
              {discounted !== null && (
                <p className="text-[12px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
                  List price: £{fmt(total)}/month · {discount}% discount applied
                </p>
              )}

              {/* Breakdown */}
              <div
                className="rounded mt-4 p-4 text-[12px] flex flex-col gap-2"
                style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)' }}
              >
                <p className="font-bold uppercase tracking-widest text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>Breakdown</p>

                {/* MS-hosted jobs */}
                {data.breakdown.parallelJobs.microsoftHosted.billableJobs > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      Microsoft-hosted jobs ({data.breakdown.parallelJobs.microsoftHosted.billableJobs} billable × £{fmt(data.breakdown.parallelJobs.microsoftHosted.ratePerJob)})
                    </span>
                    <span className="font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
                      £{fmt(data.breakdown.parallelJobs.microsoftHosted.cost)}
                    </span>
                  </div>
                )}
                {data.breakdown.parallelJobs.microsoftHosted.freeJobs > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-tertiary)' }}>
                      MS-hosted jobs: {data.breakdown.parallelJobs.microsoftHosted.freeJobs} free (1,800 min/month)
                    </span>
                    <span className="font-mono" style={{ color: 'var(--text-tertiary)' }}>£0.00</span>
                  </div>
                )}

                {/* Self-hosted jobs */}
                {data.breakdown.parallelJobs.selfHosted.billableJobs > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      Self-hosted jobs ({data.breakdown.parallelJobs.selfHosted.billableJobs} billable × £{fmt(data.breakdown.parallelJobs.selfHosted.ratePerJob)})
                    </span>
                    <span className="font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
                      £{fmt(data.breakdown.parallelJobs.selfHosted.cost)}
                    </span>
                  </div>
                )}
                {data.breakdown.parallelJobs.selfHosted.freeJobs > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-tertiary)' }}>Self-hosted: 1 free job (unlimited minutes)</span>
                    <span className="font-mono" style={{ color: 'var(--text-tertiary)' }}>£0.00</span>
                  </div>
                )}

                {/* Basic users */}
                {data.breakdown.users.billableBasicUsers > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      Basic users ({data.breakdown.users.billableBasicUsers} billable × £{fmt(data.breakdown.users.basicRatePerUser)})
                    </span>
                    <span className="font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
                      £{fmt(data.breakdown.users.basicCost)}
                    </span>
                  </div>
                )}
                {data.breakdown.users.freeBasicUsers > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-tertiary)' }}>
                      Basic users: {data.breakdown.users.freeBasicUsers} free
                    </span>
                    <span className="font-mono" style={{ color: 'var(--text-tertiary)' }}>£0.00</span>
                  </div>
                )}

                {/* Test Plan users */}
                {data.breakdown.users.testPlanUsers > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      Test Plans ({data.breakdown.users.testPlanUsers} × £{fmt(data.breakdown.users.testPlanRatePerUser)})
                    </span>
                    <span className="font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
                      £{fmt(data.breakdown.users.testPlanCost)}
                    </span>
                  </div>
                )}

                {/* Artifact storage */}
                {data.breakdown.artifactStorage.billableGB > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      Artifact storage ({data.breakdown.artifactStorage.billableGB} GB × £{fmt(data.breakdown.artifactStorage.ratePerGB)}/GB)
                    </span>
                    <span className="font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
                      £{fmt(data.breakdown.artifactStorage.cost)}
                    </span>
                  </div>
                )}
                {data.breakdown.artifactStorage.freeGB >= 2 && data.breakdown.artifactStorage.billableGB === 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-tertiary)' }}>Artifact storage: within 2 GB free tier</span>
                    <span className="font-mono" style={{ color: 'var(--text-tertiary)' }}>£0.00</span>
                  </div>
                )}

                <div
                  className="flex justify-between pt-2 mt-1 font-bold"
                  style={{ borderTop: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
                >
                  <span>Total</span>
                  <span className="font-mono">£{fmt(discounted ?? total)}/month</span>
                </div>
              </div>

              {/* Formula disclosure */}
              <details className="mt-4">
                <summary
                  className="text-[12px] cursor-pointer"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  Show formula
                </summary>
                <div
                  className="mt-2 rounded p-3 text-[11px] font-mono leading-relaxed"
                  style={{ backgroundColor: 'var(--surface-container-low)', color: 'var(--text-secondary)' }}
                >
                  <p>MS-hosted: max(0, {inputs.microsoftHostedJobs} - 1) × £{fmt(data.breakdown.parallelJobs.microsoftHosted.ratePerJob)} = £{fmt(data.breakdown.parallelJobs.microsoftHosted.cost)}</p>
                  <p>Self-hosted: max(0, {inputs.selfHostedJobs} - 1) × £{fmt(data.breakdown.parallelJobs.selfHosted.ratePerJob)} = £{fmt(data.breakdown.parallelJobs.selfHosted.cost)}</p>
                  <p>Basic users: max(0, {inputs.basicUsers} - 5) × £{fmt(data.breakdown.users.basicRatePerUser)} = £{fmt(data.breakdown.users.basicCost)}</p>
                  {inputs.testPlanUsers > 0 && (
                    <p>Test Plans: {inputs.testPlanUsers} × £{fmt(data.breakdown.users.testPlanRatePerUser)} = £{fmt(data.breakdown.users.testPlanCost)}</p>
                  )}
                  <p>Storage: max(0, {inputs.artifactStorageGB} - 2) GB × £{fmt(data.breakdown.artifactStorage.ratePerGB)} = £{fmt(data.breakdown.artifactStorage.cost)}</p>
                  <p className="mt-1 font-bold">Total: £{fmt(total)}/month</p>
                  <p className="mt-2 text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                    {data.pricingNote}
                  </p>
                </div>
              </details>

              <DiscountModifier discount={discount} onDiscountChange={setDiscount} paygTotal={total} />

              <div className="mt-4">
                <ShareButton inputs={inputs} result={data} service="azure-devops" />
              </div>

              <a href="https://azure.microsoft.com/en-gb/pricing/details/devops/azure-devops-services/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono mt-2" style={{ color: 'var(--text-tertiary)' }}>
                <span className="material-symbols-outlined text-[13px]">open_in_new</span>
                Verify on official Azure pricing page
              </a>

              <p className="text-[11px] mt-4" style={{ color: 'var(--text-tertiary)' }}>
                Prices verified {data.prices_last_updated ? new Date(data.prices_last_updated).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}. Estimates only — see disclaimer.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
